import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Wrench, ShoppingCart, Check, ArrowRight } from 'lucide-react';
import { productSlug } from '@/utils/slugify';

export default function SparePartsList({ product }) {
  const { addItem } = useCart();
  const [addedId, setAddedId] = useState(null);

  const { data: parts = [], isLoading } = useQuery({
    queryKey: ['cs-spare-parts', product?.id],
    enabled: !!product,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      // Uniquement la sélection manuelle (admin) ; aucune détection automatique
      if (!product.spare_parts?.length) return [];
      const found = await Promise.all(
        product.spare_parts.slice(0, 30).map(id => base44.entities.Product.get(id).catch(() => null))
      );
      return found.filter(Boolean);
    },
  });

  if (isLoading || parts.length === 0) return null;

  const visible = parts.slice(0, 10);

  const handleAdd = (part) => {
    if (part.stock_quantity === 0) return;
    addItem(part);
    setAddedId(part.id);
    setTimeout(() => setAddedId(id => (id === part.id ? null : id)), 2000);
  };

  return (
    <div className="mt-16 pt-10 border-t border-gray-100">
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="w-5 h-5 text-cs-blue" />
        <h2 className="font-display font-bold text-xl text-gray-900">PIÈCES DÉTACHÉES COMPATIBLES</h2>
      </div>
      <p className="text-sm text-gray-500 mb-6">Pièces sélectionnées pour votre {product.name} — ajoutez-les en un clic à votre commande.</p>

      <div className="rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
        {visible.map(part => {
          const outOfStock = part.stock_quantity === 0;
          return (
            <div key={part.id} className={`flex items-center gap-3 px-4 py-3 ${outOfStock ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'} transition-colors`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${outOfStock ? 'bg-red-400' : 'bg-green-500'}`} aria-hidden="true" />
              <Link to={`/product/${productSlug(part)}`} className="w-10 h-10 rounded bg-gray-100 overflow-hidden shrink-0 hidden sm:block">
                {part.thumbnail_url || part.image_url ? (
                  <img src={part.thumbnail_url || part.image_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain" />
                ) : null}
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {part.reference && (
                    <span className="text-[10px] font-bold text-cs-blue bg-cs-blue/10 px-1.5 py-0.5 rounded">{part.reference}</span>
                  )}
                  {outOfStock && <span className="text-[10px] font-bold text-red-500">RUPTURE</span>}
                </div>
                <Link to={`/product/${productSlug(part)}`} className="block text-sm font-medium text-gray-800 truncate hover:text-cs-blue transition-colors">
                  {part.name}
                </Link>
              </div>
              <span className="font-bold text-gray-900 text-sm shrink-0">{part.price?.toFixed(2)} €</span>
              <button
                onClick={() => handleAdd(part)}
                disabled={outOfStock}
                aria-label={`Ajouter ${part.name} au panier`}
                className={`shrink-0 flex items-center justify-center w-9 h-9 rounded-lg font-bold transition-colors ${
                  outOfStock ? 'bg-gray-100 text-gray-300 cursor-not-allowed' :
                  addedId === part.id ? 'bg-green-500 text-white' :
                  'bg-cs-blue text-white hover:bg-cs-blue-dark'
                }`}
              >
                {addedId === part.id ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
              </button>
            </div>
          );
        })}
      </div>

      {parts.length > 10 && (
        <Link to="/shop?cat=pieces-detachees" className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold text-cs-blue hover:underline">
          Voir toutes les pièces détachées <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}